/** Read/write dotted paths like sim.friendStep on a bag of objects. */

export type PathRoot = Record<string, Record<string, unknown>>;

export function getPath(root: PathRoot, path: string): unknown {
  const [head, ...rest] = path.split(".");
  if (!head || rest.length === 0) return undefined;
  let cur: unknown = root[head];
  for (const key of rest) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

export function setPath(root: PathRoot, path: string, value: unknown): void {
  const parts = path.split(".");
  if (parts.length < 2) return;
  const head = parts[0]!;
  let cur = root[head] as Record<string, unknown> | undefined;
  if (!cur) return;
  for (let i = 1; i < parts.length - 1; i++) {
    const next = cur[parts[i]!];
    if (next == null || typeof next !== "object") return;
    cur = next as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]!] = value;
}

/** First segment of a dotted path (`sim.friendStep` → `sim`). */
export function pathRoot(path: string): string | undefined {
  const head = path.split(".")[0];
  return head || undefined;
}

/** Leaf key (`sim.friendStep` → `friendStep`). */
export function pathLeaf(path: string): string | undefined {
  const parts = path.split(".");
  if (parts.length < 2) return undefined;
  return parts[parts.length - 1];
}

/**
 * Immutable-style assign on a plain object.
 *
 * Accepts either:
 *   - full control paths: `sim.friendStep` / `render.pointSize` (strips the
 *     first segment — caller already holds the sim or render object)
 *   - relative paths: `friendStep` / `typeRules.preset`
 *
 * Returns a shallow-cloned object (nested objects along the path are also
 * cloned). Does not mutate `obj`.
 */
export function assignPathClone<T extends Record<string, unknown>>(
  obj: T,
  path: string,
  value: unknown,
): T {
  if (!path) return obj;
  const parts = path.split(".").filter(Boolean);
  if (parts.length === 0) return obj;

  // Strip bag prefix when present so callers can pass full schema paths
  const rel =
    parts[0] === "sim" || parts[0] === "render" || parts[0] === "audio"
      ? parts.slice(1)
      : parts;
  if (rel.length === 0) return obj;

  const next = { ...obj } as T;
  if (rel.length === 1) {
    (next as Record<string, unknown>)[rel[0]!] = value;
    return next;
  }

  let cur: Record<string, unknown> = next as Record<string, unknown>;
  for (let i = 0; i < rel.length - 1; i++) {
    const key = rel[i]!;
    const child = cur[key];
    const cloned =
      child != null && typeof child === "object" && !Array.isArray(child)
        ? { ...(child as Record<string, unknown>) }
        : {};
    cur[key] = cloned;
    cur = cloned;
  }
  cur[rel[rel.length - 1]!] = value;
  return next;
}
